/**
 * Add a tenant.
 *
 * There was no way to do this from the UI at all — accounts only existed if
 * somebody re-ran the seed script, which is a fine way to lose a Friday.
 *
 * Two things this form does deliberately:
 *
 * - **It derives what it can.** Given `equentis.8loop.ai` it fills in the API base
 *   URL and the slug. Those are mechanical transformations of the host; asking a
 *   human to type them is asking them to make a typo that surfaces later as a
 *   confusing 404 from someone else's tenant.
 * - **It requires the API key here, at the start.** Not a UI opinion: both
 *   `accountCreateSchema` and a CHECK constraint on `accounts` refuse a row with
 *   neither a key nor a login, and an account that cannot authenticate cannot even
 *   list the tenant's agents — so there is nothing you could usefully configure
 *   next. Better to be asked now than to be 400'd after filling in four fields.
 */

import { useEffect, useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CopyableValue } from "@/components/ui/copyable-value";
import { errorMessage } from "@/lib/api";
import { useCreateAccount } from "@/lib/queries";

/** `equentis.8loop.ai` → `equentis`; falls back to a slugified name. */
function slugFromHost(host: string, name: string): string {
  const first = host.trim().toLowerCase().split(".")[0] ?? "";
  const candidate = /^[a-z0-9][a-z0-9-]*$/.test(first) ? first : "";
  if (candidate) return candidate;
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

const COMMON_TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Singapore",
  "Europe/London",
  "America/New_York",
  "UTC",
];

export function NewAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateAccount();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [host, setHost] = useState("");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [countryCode, setCountryCode] = useState("+91");
  const [apiKey, setApiKey] = useState("");
  /** The ingest token, shown exactly once after a successful create. */
  const [issued, setIssued] = useState<{ token: string; url: string; id: string } | null>(null);

  // A dialog that remembers half-typed values from the last account is a trap.
  useEffect(() => {
    if (!open) return;
    setName("");
    setHost("");
    setTimezone("Asia/Kolkata");
    setCountryCode("+91");
    setApiKey("");
    setIssued(null);
    create.reset();
    // `create` is a stable mutation object; re-running on its identity would clear
    // the form mid-submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanHost = host.trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  const baseUrl = cleanHost ? `https://${cleanHost}` : "";
  const slug = slugFromHost(cleanHost, name);
  const canSubmit =
    Boolean(name.trim() && cleanHost && slug && apiKey.trim()) && !create.isPending;

  /*
    What is still missing, in words. A greyed-out "Create account" with nothing beside
    it makes people re-read four filled-in fields looking for the one that isn't —
    worst of all for the slug, which nobody typed and which can be empty even with a
    host present (`123.8loop.ai` fails the leading-letter rule).
  */
  const missing = [
    !name.trim() && "a name",
    !cleanHost && "the 8loop host",
    Boolean(cleanHost) && !slug && "a slug — this host's first label is not usable as one",
    !apiKey.trim() && "the API key",
  ].filter((m): m is string => typeof m === "string");

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        name: name.trim(),
        slug,
        tenant_host: cleanHost,
        base_url: baseUrl,
        timezone,
        default_country_code: countryCode.trim() || null,
        // Required: this form does not offer the email+password mode, so the key
        // is the only way to satisfy the server's "one credential or the other"
        // rule. It is also the better half of that choice — API-key auth is the
        // only mode where 8loop honours Idempotency-Key.
        api_key: apiKey.trim(),
      },
      {
        onSuccess: (r) => {
          setIssued({ token: r.ingest_token, url: r.ingest_url, id: r.account.id });
          toast.success(`${r.account.name} created.`);
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Once the token is on screen it is unrecoverable — only its hash is stored — so
        the token step deliberately stops being dismissible the three casual ways a
        dialog normally is: Escape, a click on the overlay, and the X in the corner.
        Losing it costs a token rotation and a second trip into the tenant's code
        action, which is a lot to pay for a stray keypress. The two footer buttons
        remain the only way out, and both are deliberate.
      */}
      <DialogContent
        className="sm:max-w-lg"
        showCloseButton={!issued}
        onEscapeKeyDown={(e) => {
          if (issued) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (issued) e.preventDefault();
        }}
      >
        {issued ? (
          <>
            <DialogHeader>
              <DialogTitle>The account is ready</DialogTitle>
              {/*
                This used to say "Only its hash is stored, so this is the one time it can be
                read", and then told the FDE to paste it into a code action. Both halves stopped
                being true at migration 016: the token is stored encrypted as well as hashed, the
                app reads it back on every plan load, and it is baked into each brief the app
                generates. Asking somebody to keep a copy of a secret the app issued and can
                read is asking them to do the app's job.
              */}
              <DialogDescription>
                Stored encrypted, and put into every brief this app generates — so you never need
                to copy it. It is here in case you want it now.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <CopyableValue label="Ingest URL" value={issued.url} mono />
              <CopyableValue label="Ingest token" value={issued.token} mono />
              {/*
                This used to say "no routing. Nothing will dial until you tell it which
                channel to call back on." That was true of the rules engine and has been
                false since routing became identity: a callback goes out on the channel it
                came in on, with no configuration at all. Telling an FDE to go and fix a
                setting that is already correct sends them looking for a problem that does
                not exist — and the real blocker, the code action that has to post to us,
                went unmentioned.
              */}
              <Alert>
                <KeyRound className="size-4" />
                <AlertDescription>
                  Calling hours start at Monday–Saturday, 09:00–18:00, and callbacks dial back
                  out on whichever channel they arrived from — routing needs nothing unless this
                  tenant wants an exception. What it does still need is a post-call hook on each
                  agent you want callbacks from: add the agent under Configuration → Agents and
                  hand its brief to your Claude Code. Until one posts a finished call, nothing
                  arrives here.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/accounts/${issued.id}/settings`);
                }}
              >
                Finish setting it up
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>New account</DialogTitle>
              <DialogDescription>
                One 8loop tenant. Everything else — calling hours, routing, context — is configured
                per account afterwards.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <Field label="Name" htmlFor="acct-name" hint="What the FDE team calls this customer.">
                <Input
                  id="acct-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Equentis"
                  autoFocus
                />
              </Field>

              <Field
                label="8loop host"
                htmlFor="acct-host"
                hint={
                  baseUrl
                    ? `API base URL will be ${baseUrl} · slug ${slug}`
                    : "The tenant's own domain, e.g. equentis.8loop.ai"
                }
              >
                <Input
                  id="acct-host"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="equentis.8loop.ai"
                  className="font-mono text-xs"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Timezone" htmlFor="acct-tz" hint="Calling hours are read in this zone.">
                  <Input
                    id="acct-tz"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    list="tz-suggestions"
                    className="font-mono text-xs"
                  />
                  <datalist id="tz-suggestions">
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz} />
                    ))}
                  </datalist>
                </Field>
                <Field
                  label="Default country code"
                  htmlFor="acct-cc"
                  hint="Used when a number arrives without one."
                >
                  <Input
                    id="acct-cc"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    placeholder="+91"
                    className="font-mono text-xs"
                  />
                </Field>
              </div>

              <Field
                label="8loop API key"
                htmlFor="acct-key"
                hint="Mint one in the tenant at Settings → API keys, with the INITIATE_CALLS scope. Stored encrypted; only the last few characters are ever shown again."
              >
                <Input
                  id="acct-key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="8l_live_…"
                  className="font-mono text-xs"
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>

              {!apiKey.trim() && (
                <Alert>
                  <KeyRound className="size-4" />
                  <AlertDescription>
                    A key is required — without one the app cannot authenticate against the tenant,
                    so it can neither list the agents nor place a call.
                  </AlertDescription>
                </Alert>
              )}

              {create.isError && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage(create.error)}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              {missing.length > 0 && (
                <p className="mr-auto self-center text-[11px] text-muted-foreground">
                  Still needed: {missing.join(", ")}
                </p>
              )}
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={!canSubmit} onClick={submit}>
                {create.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Create account
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * A labelled field.
 *
 * `htmlFor` is required, not optional. Every one of these labels used to be a bare
 * `<Label>` pointing at nothing, which looks identical and behaves differently: the
 * label text is not a click target for the input, and a screen reader announces five
 * unlabelled text boxes. Making the prop mandatory means the next field added here
 * cannot quietly repeat it.
 */
function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
